import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid2, IconButton, Tooltip, Typography } from '@mui/material';
import { Help as HelpIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { get, isString } from 'lodash';
import { HexColorPicker } from 'react-colorful';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { getExchangeNetwork, editExchangeNetwork } from './_apiExchangeNetworks';
import { IExchangeNetwork, IExchangeNetworkFormContent } from './_types';
import useValidationSchema from './_formExchangeNetwork';

import { useWithEntityTitle } from 'utils/hooks/useWithEntityTitle';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';

import Header from 'components/Header/Header';
import { Papeer } from 'components/Papeer/Papeer';
import FormInput from 'components/Form/Input/Input';
import { EntityButtons } from 'components/Form/EntityButtons/EntityButtons';
import useAlerts from 'components/Alerts/useAlerts';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { Box } from '@mui/system';
import { PriorityButtons } from './PriorityButtons';

const ExchangeNetworkForm: React.FC<IExchangeNetworkFormContent> = ({
  toggleDialog,
  id,
  loadEntity,
}) => {
  const { t } = useTranslation('ExchangeNetworks');
  const { toggleLoader } = useAppGlobals();
  const { addErrorAlert, addSuccessAlert } = useAlerts();
  const { compactMode } = useAppInfo();

  const [entity, fetchEntity] = useState<IExchangeNetwork>();
  const [colorPickerVisible, setColorPickerVisible] = useState<boolean>(false);
  const [passwordShown, setPasswordShown] = useState<boolean>(false);
  const { title } = useWithEntityTitle(false, entity, t);

  const { ExchangeNetworkFormSchema, formItems } = useValidationSchema(t);

  const methods = useForm<IExchangeNetwork>({
    resolver: yupResolver(ExchangeNetworkFormSchema),
  });
  const { handleSubmit, reset, register, watch, setValue } = methods;
  const watchColor = watch('color');
  const selectedPriority = watch('priority');
  const setPriority = (priority: any) => setValue('priority', priority);

  const onSubmit = handleSubmit(async (values) => {
    toggleLoader();
    const response = await editExchangeNetwork(values);
    if (response === true) {
      addSuccessAlert(t('saved'));
      loadEntity();
      toggleDialog(false);
    } else {
      addErrorAlert(t(isString(response) ? response : 'errorSaving'));
    }
    toggleLoader(false);
  });

  const getEntity = async () => {
    toggleLoader();
    try {
      let exchangeNetwork: IExchangeNetwork = { id: 0 };

      if (id) {
        const entity = await getExchangeNetwork(id.toString());
        exchangeNetwork = { ...entity };
        if (entity) {
          fetchEntity(exchangeNetwork);
        }
      }

      reset({ ...exchangeNetwork });
    } catch (e) {
      console.debug(e);
    }

    toggleLoader(false);
  };

  useEffect(() => {
    getEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setColor = (color: string) => {
    setValue('color', color);
  };

  const toggleColorPicker = () => {
    setColorPickerVisible(!colorPickerVisible);
  };
  return (
    <>
      {entity ? (
        <>
          <Header title={title} />
          <Papeer>
            <FormProvider {...methods}>
              <form onSubmit={onSubmit}>
                <input {...register('id')} type="hidden" />
                <input {...register('active')} type="hidden" />
                <input {...register('name')} type="hidden" />
                <Grid2 container={true} alignItems="flex-end">
                  <Grid2 size={{ xs: 12 }}>
                    {formItems.map((formItem, index) => {
                      const name = get(formItem, 'name', '');
                      return (
                        <React.Fragment key={`formItem-${index}`}>
                          <div style={{ position: 'relative' }}>
                            {name === 'color' && (
                              <>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    right: (theme) => theme.spacing(4),
                                    top: (theme) =>
                                      compactMode ? theme.spacing(1.4) : theme.spacing(1.8),
                                    width: (theme) => theme.spacing(4),
                                    height: (theme) => theme.spacing(3),
                                    border: '1px solid #000',
                                    zIndex: 69,
                                    cursor: 'pointer',
                                    backgroundColor: `${watchColor}`,
                                  }}
                                  onClick={toggleColorPicker}
                                />
                              </>
                            )}
                            {name === 'password' && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  right: (theme) => theme.spacing(4),
                                  top: (theme) =>
                                    compactMode ? theme.spacing(0.6) : theme.spacing(1),
                                  zIndex: 69,
                                }}
                              >
                                <IconButton
                                  color="inherit"
                                  aria-label="Menu"
                                  onClick={() => setPasswordShown(!passwordShown)}
                                  style={{ padding: '8px' }}
                                >
                                  {passwordShown ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </Box>
                            )}
                            <FormInput
                              {...formItem}
                              type={passwordShown || name !== 'password' ? 'text' : 'password'}
                            />
                            {name === 'color' && colorPickerVisible && (
                              <HexColorPicker color={watchColor} onChange={setColor} />
                            )}
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </Grid2>
                  <Grid2 container={true} alignItems="flex-end">
                    <Grid2 size={12} sx={{ mb: 2 }}>
                      {t('priority')}
                      <Tooltip title={t('priorityHelp') as string} style={{ cursor: 'pointer' }}>
                        <HelpIcon fontSize="small" />
                      </Tooltip>
                      <PriorityButtons
                        priority={selectedPriority ? selectedPriority : null}
                        setPriority={setPriority}
                      />
                    </Grid2>

                    <Grid2 container={true} alignItems="flex-end">
                      <EntityButtons toggleDialog={toggleDialog} />
                    </Grid2>
                  </Grid2>
                </Grid2>
              </form>
            </FormProvider>
          </Papeer>
        </>
      ) : (
        <Papeer>
          <Typography>{t('exchangeNetworkNotFound')}</Typography>
        </Papeer>
      )}
    </>
  );
};

export default ExchangeNetworkForm;
